import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { MinigameStateSnapshot } from '../../shared/events';

const popFade = keyframes`
  0%   { transform: scale(0.5); opacity: 0; }
  40%  { transform: scale(1.2); opacity: 1; }
  70%  { transform: scale(1);   opacity: 1; }
  100% { transform: scale(0.8); opacity: 0; }
`;

const StepText = styled(Typography)`
  animation: ${popFade} 0.9s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
  font-family: "Comic Sans MS", cursive, sans-serif;
  font-weight: bold;
  text-shadow: 3px 3px 0 rgba(0,0,0,0.4);
`;

const RULES: Record<string, string> = {
  'closest-number': 'Guess the closest number to the secret target (1–100)!',
  'number-question': 'Answer the question — lowest or highest wins!',
  'pick-a-side': 'Pick a side — the minority wins!',
  'fastest-tap': 'Tap as fast as you can when the button appears!',
  'type-the-word': 'Type the displayed word as fast as you can!',
  'vote-boost': 'Vote for the player you want to protect!',
  'team-red-blue': 'Pick your team — one team wins randomly!',
};

interface Props {
  minigame: MinigameStateSnapshot;
}

const MinigameCountdown: React.FC<Props> = ({ minigame }) => {
  const [step, setStep] = useState(0);
  const steps = [3, 2, 1];

  useEffect(() => {
    if (step >= steps.length) return;
    const t = setTimeout(() => setStep(s => s + 1), 900);
    return () => clearTimeout(t);
  }, [step, steps.length]);

  const current = steps[step];

  return (
    <Box
      sx={{
        position: 'fixed', inset: 0, zIndex: 700,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        gap: 3, pointerEvents: 'none',
      }}
    >
      {current !== undefined && (
        <StepText key={step} sx={{ fontSize: '9rem', color: 'white', lineHeight: 1 }}>
          {current}
        </StepText>
      )}

      <Box
        sx={{
          background: 'rgba(255,255,255,0.15)',
          borderRadius: '20px',
          px: 4, py: 2,
          border: '2px solid rgba(255,255,255,0.3)',
          maxWidth: 420, textAlign: 'center',
        }}
      >
        <Typography
          sx={{
            fontFamily: '"Comic Sans MS", cursive, sans-serif',
            color: 'white', fontSize: '1.1rem', fontWeight: 'bold',
            textShadow: '1px 1px 0 rgba(0,0,0,0.4)',
          }}
        >
          {RULES[minigame.type] ?? ''}
        </Typography>
        {minigame.question && (
          <Typography
            sx={{
              fontFamily: '"Comic Sans MS", cursive, sans-serif',
              color: '#FFD700', fontSize: '1rem', mt: 1,
              textShadow: '1px 1px 0 rgba(0,0,0,0.4)',
            }}
          >
            "{minigame.question}"
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default MinigameCountdown;
